import React, { useState, useEffect , useRef} from "react";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import axios from "axios";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const dayNamesEn = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const dayNamesUr = ["اتوار","پیر","منگل","بدھ","جمعرات","جمعہ","ہفتہ"];

const statusOptions = [
  "",
  "D/O",
  "P",
  "A",
  "M",
  "E",
  "N",
  "N/O",
  "E/O",
  "Ramzan",
  "24 Hrs Duty",
  "Eid Duty",
  "M+E",
  "OPD",
  "ITC",
  "M(10am to 4pm)"  // ✅ New option added
];


// 🔹 Urdu translation map for duty codes
const dutyMapUrdu = {
  M: "صبح",
  E: "دوپہر",
  N: "رات",
  "D/O": "چھٹی",
  P: "حاضر",
  A: "غیر حاضر",
  "N/O": "رات آف",
  "E/O": "دوپہر آف",
  Ramzan: "رمضان",
  "24 Hrs Duty": "چوبیس گھنٹے ڈیوٹی",
  "Eid Duty": "عید ڈیوٹی",
  "M+E": "صبح + دوپہر",
  OPD: "او پی ڈی",
  ITC: "آئی ٹی سی",
  "M(10am to 4pm)": "صبح (10 بجے سے 4 بجے تک)"
};


// 🔹 Helper: auto translate to Urdu
const autoTranslateToUrdu = async (text) => {
  if (!text?.trim()) return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(
        text
      )}`
    );
    const json = await res.json();
    return json[0]?.map((item) => item[0]).join("") || text;
  } catch (err) {
    console.error("Translation error:", err);
    return text;
  }
};

const generatePDFBlob = async ({ language, month, days, staff, numDays }) => {
const isUrdu = language === "ur";
const hospitalName = isUrdu
  ? "راولپنڈی انسٹی ٹیوٹ آف کارڈیالوجی"
  : "Rawalpindi Institute of Cardiology";

const subAddress = isUrdu
  ? "راول روڈ، راولپنڈی"
  : "Rawal Road, Rawalpindi";

const title = isUrdu
  ? "شعبۂ بایومیڈیکل روسٹر"
  : "Biomedical Department Roster";



  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  // 🔹 figure out Sundays
  const currentYear = new Date().getFullYear();
  const sundays = [];
  for (let i = 1; i <= numDays; i++) {
    const date = new Date(`${month} ${i}, ${currentYear}`);
    if (date.getDay() === 0) sundays.push(i);
  }

  // 🔹 headers
const headers = Array.from({ length: numDays }, (_, i) => {
  const dayNum = i + 1;
  const date = new Date(`${month} ${dayNum}, ${currentYear}`);
  const dayName = isUrdu ? dayNamesUr[date.getDay()] : dayNamesEn[date.getDay()];
  const isSunday = date.getDay() === 0;

  return `<th style="
    padding:6px; 
    border:1px solid #ccc; 
    ${isSunday ? "background:#222; color:#fff; font-weight:700;" : "background:#eaf1ff; color:#333;"}
  ">
    ${dayNum}<br/><span style="font-size:8px; color:#555;">${dayName}</span>
  </th>`;
}).join("");

  // 🔹 rows with staff name auto-translation
  const rowsHtml = await Promise.all(
    staff.map(async (person) => {
      const displayName = isUrdu ? await autoTranslateToUrdu(person) : person;

      const rowCells = Array.from({ length: numDays }, (_, i) => {
        const dayNum = i + 1;
        const isSunday = sundays.includes(dayNum);
        let value = days[person]?.[dayNum] || "";

        // ✅ Translate duty code if Urdu
        if (isUrdu && dutyMapUrdu[value]) {
          value = dutyMapUrdu[value];
        }

        return `<td style="
          padding:6px; 
          border:1px solid #ccc; 
          ${isSunday ? "background:#f5f5f5; font-weight:600;" : ""}
        ">${value}</td>`;
      }).join("");

      return `<tr>
        <td style="padding:6px; border:1px solid #ccc; font-weight:600;">
          ${displayName}
        </td>${rowCells}
      </tr>`;
    })
  );

  const rowsHtmlFinal = rowsHtml.join("");

  // 🔹 your full design content (unchanged, just inserting rowsHtmlFinal)
  const content = `
    <div style="font-family: ${
      isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"
    }; direction: ${isUrdu ? "rtl" : "ltr"}; padding: 20px; font-size: 12px; color: #2c3e50;">
      
      <!-- 🔹 header section -->
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:20px;">
        <div style="width:25%;"><img src="${leftLogoUrl}" style="height:60px;" /></div>
        <div style="text-align:center; width:60%;">
          <h2 style="margin:0; font-weight:700;">${hospitalName}</h2>
          <p style="margin:2px 0; font-size:13px; color:#555;">${subAddress}</p>
          <h4 style="margin:6px 0; color:#444;">${title} – ${month}</h4>
        </div>
        <div style="width:25%; text-align:${isUrdu ? "left" : "right"};">
          <img src="${rightLogoUrl}" style="height:60px;" />
        </div>
      </div>

      <!-- 🔹 roster table -->
      <table style="width:100%; border-collapse:collapse; text-align:center; font-size:9px;">
        <thead>
          <tr>
            <th style="padding:6px; border:1px solid #ccc; background:#0052D4; color:#fff;">
              ${isUrdu ? "عملے کا نام" : "Name of Staff"}
            </th>
            ${headers}
          </tr>
        </thead>
        <tbody>
          ${rowsHtmlFinal}
        </tbody>
      </table>


<div style="margin-top: 80px; display: flex; justify-content: space-between; font-size: 13px;">

  <!-- Left Signature (Radiology Department) -->
  <div style="text-align: center; line-height: 1.5; width: 40%;">
    <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
    <strong>${isUrdu ? "شعبۂ بایومیڈیکل" : "HOD Biomedical"}</strong><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${hospitalName}
    </span><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${isUrdu ? "راولپنڈی" : "Rawalpindi"}
    </span>
  </div>

  <!-- Right Signature (DMS Admin) -->
  <div style="text-align: center; line-height: 1.5; width: 40%;">
    <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
    <strong>${isUrdu ? "ڈاکٹر رضوان نعیم (ڈی ایم ایس ایڈمن)" : "(DMS Admin) Dr. Rizwan Naeem"}</strong><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${hospitalName}
    </span><br />
    <span style="${isUrdu ? 'direction: rtl; display: block;' : ''}">
      ${isUrdu ? "راولپنڈی" : "Rawalpindi"}
    </span>
  </div>

</div>



       <!-- 🔹 Copy To Section -->
<div style="margin-top:40px; font-size:${isUrdu ? "11px" : "11px"}; line-height:1.6; text-align:${isUrdu ? "right" : "left"}; direction:${isUrdu ? "rtl" : "ltr"};">
  <strong>${isUrdu ? "برائے نقل:" : "Copy To,"}</strong><br />
  ${isUrdu 
    ? `
    1- ایگزیکٹو ڈائریکٹر، راولپنڈی انسٹی ٹیوٹ آف کارڈیالوجی، راولپنڈی۔<br />
    2- میڈیکل سپرنٹنڈنٹ، راولپنڈی انسٹی ٹیوٹ آف کارڈیالوجی، راولپنڈی۔<br />
    3- ڈی ایم ایس (ایڈمن)، راولپنڈی انسٹی ٹیوٹ آف کارڈیالوجی، راولپنڈی۔<br />
    `
    : `
    1- The Executive Director, Rawalpindi Institute of Cardiology, Rawalpindi.<br />
    2- The Medical Superintendent, Rawalpindi Institute of Cardiology, Rawalpindi.<br />
    3- The DMS (Admin), Rawalpindi Institute of Cardiology, Rawalpindi.<br />
    `}
</div>

      <p style="text-align:center; margin-top:20px; font-size:10px; color:#888;">
        © ${currentYear} ${hospitalName}. ${isUrdu ? "جملہ حقوق محفوظ ہیں۔" : "All rights reserved."}
      </p>
    </div>
  `;

  return await html2pdf()
    .from(content)
    .set({
      margin: 0.3,
      filename: `BiomedicalDepartment_${month}_${language.toUpperCase()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
    })
    .output("blob");
};



const BiomedicalDepartment = (language) => {
  const [staffList, setStaffList] = useState([]);
  const [staff, setStaff] = useState([]); // Selected staff
  const [numDays, setNumDays] = useState(30);
      const [selectedDepartment, setSelectedDepartment] = useState("biomedical");
      const [department, setDepartment] = useState("biomedical");
   const [month, setMonth] = useState(""); // start empty, load only on user selection
  const [days, setDays] = useState({});
  const [newStaffName, setNewStaffName] = useState("");
  const [newStaffRole, setNewStaffRole] = useState("");
  const isUrdu = language === "ur";


  const [filterName, setFilterName] = useState("");
  const [eCount, setECount] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);

 const countEForStaff = (name) => {
  if (!name || !days[name]) {
    setECount(0);
    return;
  }

  const attendance = days[name]; // { "1": "P", "2": "E", ... }
  const totalE = Object.values(attendance).filter(status => status === "E").length;
  setECount(totalE);
};

  
  const [roles, setRoles] = useState([
    { value: "Ward Boy", label: "Ward Boy" },
    { value: "Aya", label: "Aya" },
    { value: "Other", label: "➕ Add New Role" },
  ]);

  const [highlightIndex, setHighlightIndex] = useState(-1);

  // Inside your component
const inputRef = useRef(null);
const [showSuggestions, setShowSuggestions] = useState(false);

 
  const roleOptions = [
  { value: "Ward Boy", label: "Ward Boy" },
  { value: "Aya", label: "Aya" },
  { value: "Hostel Clerk", label: "Hostel Clerk" },
  { value: "Waiter", label: "Waiter" },
  { value: "Other", label: "Other" }
];

const addStaff = () => {
  if (!newStaffName.trim() || !newStaffRole) {
    return Swal.fire("⚠️ Please enter name and role", "", "warning");
  }

  // Prevent duplicate staff
  const exists = staffList.some(
    (s) => s.name.toLowerCase() === newStaffName.toLowerCase()
  );
  if (exists) {
    return Swal.fire(
      "⚠️ Already Exists",
      "This staff member is already in the list.",
      "warning"
    );
  }

  axios.post("https://ricgop.online/backend/staff/addStaff.php", {
    name: newStaffName,
    role: newStaffRole.value || newStaffRole,
  })
  .then(() => {
    Swal.fire("✅ Added!", "Staff saved successfully", "success");

    // Add staff to staffList
    setStaffList(prev => [
      ...prev,
      { name: newStaffName, role: newStaffRole.value || newStaffRole },
    ]);

    // Add staff to selected staff and generate default days if month is selected
    setStaff(prev => {
      if (prev.includes(newStaffName)) return prev;
      return [...prev, newStaffName];
    });

    setDays(prevDays => {
      if (!month) return prevDays; // if month not selected, don't generate days
      const newDays = { ...prevDays };
      if (!newDays[newStaffName]) {
        const defaultDays = generateDefaultDays([newStaffName], month, numDays)[newStaffName];
        newDays[newStaffName] = defaultDays;
      }
      return newDays;
    });

    setNewStaffName("");
    setNewStaffRole(null);
  })
  .catch(() => {
    Swal.fire("❌ Error", "Failed to add staff", "error");
  });
};


  useEffect(() => {
  if (month) fetchRoster(null, month);
}, [month]);

  useEffect(() => {
  if (month) {
    // Call your fetchRoster function whenever month changes
    fetchRoster(null, month);
  }
}, [month]);

  useEffect(() => {
    axios.get("https://ricgop.online/backend/staff/getStaff.php")
      .then(res => {
        setStaffList(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setStaffList([]));
  }, []);


//   removed this use effect bcz of override db

//  useEffect(() => {
//   const temp = {};
//   staff.forEach((person) => {
//     temp[person] = {};
//     for (let i = 1; i <= numDays; i++) {
//       const [monthName, year] = month.split(" ");
//       const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
//       const date = new Date(year, monthIndex, i);
//       temp[person][i] = date.getDay() === 0 ? "D/O" : "E";
//     }
//   });
//   setDays(temp);
// }, [staff, numDays, month]);

// useEffect(() => {
//   if (Object.keys(days).length > 0) return; // Already loaded from DB

//   if (!month || staff.length === 0) return; // Only run if month and staff selected

//   const temp = {};
//   staff.forEach((person) => {
//     temp[person] = {};
//     for (let i = 1; i <= numDays; i++) {
//       const [monthName, year] = month.split(" ");
//       const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
//       const date = new Date(year, monthIndex, i);
//       temp[person][i] = date.getDay() === 0 ? "D/O" : "E";
//     }
//   });
//   setDays(temp);
// }, [staff, numDays, month, days]);

// Populate default days when month is selected or new staff added
// 1) Auto-set numDays when user selects a month
useEffect(() => {
  if (!month) return;

  // month expected like "January 2026"
  const parts = month.trim().split(" ");
  const yearStr = parts.pop();
  const monthName = parts.join(" ");
  const yearNum = parseInt(yearStr, 10);

  if (!monthName || isNaN(yearNum)) return;

  const monthIndex = new Date(`${monthName} 1, ${yearNum}`).getMonth();
  if (isNaN(monthIndex)) return;

  const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
  setNumDays(daysInMonth);
}, [month]);

// 2) Sync the `days` object when staff / month / numDays change.
//    Adds default entries for newly-required days and trims extras.
useEffect(() => {
  if (!month || staff.length === 0) return;

  setDays(prev => {
    const next = { ...prev };

    const parts = month.trim().split(" ");
    const yearStr = parts.pop();
    const monthName = parts.join(" ");
    const yearNum = parseInt(yearStr, 10);
    const monthIndex = new Date(`${monthName} 1, ${yearNum}`).getMonth();

    staff.forEach(person => {
      if (!next[person]) next[person] = {};

      // Add any missing days up to numDays with your default logic
      for (let d = 1; d <= numDays; d++) {
        if (next[person][d] === undefined) {
          const date = new Date(yearNum, monthIndex, d);
          next[person][d] = date.getDay() === 0 ? "D/O" : "E";
        }
      }

      // Remove extra days if numDays decreased
      Object.keys(next[person]).forEach(k => {
        const dayNum = Number(k);
        if (dayNum > numDays) delete next[person][k];
      });
    });

    return next;
  });
}, [numDays, staff, month]);

const holidayMap = {
  "March 2025": { 31: "Eid-ul-Fitr" },
  "April 2025": { 1: "Eid-ul-Fitr", 2: "Eid-ul-Fitr" },
  "June 2025": { 7: "Eid-ul-Adha", 8: "Eid-ul-Adha", 9: "Eid-ul-Adha" },
  "September 2025": {
    5: "Eid Milad-un-Nabi",
    6: "Defence Day",
    7: "Air Force Day",
  },
  // Add other years/months as needed
};

const generateDefaultDays = (staffArray, monthStr, numDays) => {
  const temp = {};
  staffArray.forEach((person) => {
    temp[person] = {};
    for (let i = 1; i <= numDays; i++) {
      const [monthName, year] = monthStr.split(" ");
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
      const date = new Date(year, monthIndex, i);
      temp[person][i] = date.getDay() === 0 ? "D/O" : "E"; // Sunday = D/O
    }
  });
  return temp;
};


  const updateDay = (name, dayNum, value) => {
    setDays(prev => ({
      ...prev,
      [name]: { ...prev[name], [dayNum]: value }
    }));
  };


  useEffect(() => {
  const handleClickOutside = (event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setShowSuggestions(false); // close dropdown
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

  const downloadPDFs = async () => {
    if (!staff.length) return Swal.fire("⚠️ Select at least one staff member", "", "warning");
    Swal.fire({ title: 'Generating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const [enBlob, urBlob] = await Promise.all([
        generatePDFBlob({ language: "en", month, days, staff, numDays }),
        generatePDFBlob({ language: "ur", month, days, staff, numDays })
      ]);

      const link = document.createElement("a");
      link.href = URL.createObjectURL(enBlob);
      link.download = `BiomedicalDepartment_${month}_EN.pdf`;
      link.click();

      const link2 = document.createElement("a");
      link2.href = URL.createObjectURL(urBlob);
      link2.download = `BiomedicalDepartment_${month}_UR.pdf`;
      link2.click();

      Swal.fire("✅ Success", "PDFs downloaded.", "success");
    } catch {
      Swal.fire("❌ Error", "PDF generation failed.", "error");
    }
  };

const handleStaffSelect = (option) => {
  setSelectedStaff(option);
  
  if (!option || !days[option.value]) {
    setECount(0);
    return;
  }

  const attendance = days[option.value]; // { "1": "P", "2": "E", ... }
  const totalE = Object.values(attendance).filter(status => status === "E").length;
  setECount(totalE);
};


   // Handle role selection
  const handleRoleChange = (selectedRole) => {
    if (selectedRole.value === "Other") {
      Swal.fire({
        title: "Enter New Role",
        input: "text",
        inputPlaceholder: "Enter role name",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed && result.value.trim() !== "") {
          const newRole = { value: result.value, label: result.value };
          setRoles((prev) => [
            ...prev.filter((r) => r.value !== "Other"),
            newRole,
            { value: "Other", label: "➕ Add New Role" },
          ]);
          setNewStaffRole(newRole);
        }
      });
    } else {
      setNewStaffRole(selectedRole);
    }
  };

  // Filtered suggestions
  const filteredSuggestions = staffList
    .filter((st) => st.name.toLowerCase().includes(newStaffName.toLowerCase()))
    .slice(0, 5);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredSuggestions[highlightIndex]) {
        setNewStaffName(filteredSuggestions[highlightIndex].name);
        setShowSuggestions(false);
        setHighlightIndex(-1);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightIndex(-1);
    }
  };

const saveRosterToDB = () => {
  if (!staff.length) {
    Swal.fire("⚠️ No staff selected", "Please add or select staff before saving", "warning");
    return;
  }

  Swal.fire({ title: "Saving roster...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

  // 🧹 Filter out removed staff from days before sending
  const cleanedDays = Object.keys(days)
    .filter(name => staff.includes(name)) // only keep selected staff
    .reduce((obj, name) => {
      obj[name] = days[name];
      return obj;
    }, {});

  axios.post("https://ricgop.online/backend/staff/api/store-roster.php", {
    department,
    month,
    numDays,
    days: cleanedDays, // use cleaned version
  })
  .then(res => {
    if (res.data.success) {
      Swal.fire("✅ Saved!", `Roster ID: ${res.data.roster_id}`, "success");
    } else {
      Swal.fire("❌ Error", res.data.message, "error");
    }
  })
  .catch(() => {
    Swal.fire("❌ Error", "Failed to save roster", "error");
  });
};


const fetchRoster = (rosterId = null, monthValue = null) => {
  const department = selectedDepartment?.toLowerCase(); // e.g. "ecg", "hvac"
  if (!department) {
    Swal.fire("⚠️ Error", "Department not selected", "error");
    return;
  }

  let url = `https://ricgop.online/backend/staff/api/show-roster.php?department=${department}`;

  if (rosterId) {
    url += `&roster_id=${rosterId}`;
  } else if (monthValue) {
    url += `&month=${encodeURIComponent(monthValue)}`;
  }

  Swal.fire({
    title: "Loading roster...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  axios.get(url)
    .then(res => {
      if (res.data.success) {
        setMonth(res.data.month);
        setNumDays(res.data.numDays);
        setDays(res.data.days);
        setStaff(Object.keys(res.data.days));
      } else {
        setDays({});
        setStaff([]);
      }
      Swal.close();
    })
    .catch(() => {
      Swal.fire("❌ Error", "Failed to fetch roster", "error");
    });
};


return (
  <div className="container-fluid bg-white p-5 rounded-4 shadow-lg border " id="expand-view">
    <h3 className="text-2xl font-bold text-center mb-5 text-gray-800">
      🩺 Biomedical Department Roster
    </h3>

    {/* Month + Days */}
    <div className="row g-4 mb-4">
      <div className="col-md-3">
        <label className="form-label fw-semibold text-gray-700">📅 Month</label>
        <select
          className="form-select rounded-3 shadow-sm"
          value={month}
          onChange={e => {
            const selectedMonth = e.target.value;
            setMonth(selectedMonth);
            if (selectedMonth) fetchRoster(null, selectedMonth);
          }}
        >
          <option value="">-- Select Month --</option>
          {Array.from({ length: 12 }, (_, i) => {
            const d = new Date();
            d.setMonth(d.getMonth() + i);
            const label = d.toLocaleString("default", { month: "long", year: "numeric" });
            return <option key={i} value={label}>{label}</option>;
          })}
        </select>
      </div>

{/* <div className="col-md-3">
  <label className="form-label fw-semibold text-gray-700">🗓 Number of Days</label>
  <input
    type="number"
    className="form-control rounded-3 shadow-sm"
    min={1}
    max={31}
    value={numDays}
    disabled // 👈 disables editing
    readOnly // 👈 extra safety
  />
</div> */}
    </div>

    {/* Select Staff */}
    <div className="mb-4">
      <label className="form-label fw-semibold text-gray-700">👨‍⚕️ Select Staff</label>
      <Select
        isMulti
        options={staffList.map(st => ({ value: st.name, label: `${st.name} – ${st.role}` }))}
        value={staff.map(name => ({
          value: name,
          label: `${name} – ${staffList.find(s => s.name === name)?.role || ""}`
        }))}
        onChange={(selected) => setStaff(selected.map(opt => opt.value))}
        placeholder="Choose staff..."
        classNamePrefix="select"
      />
    </div>

    {/* Add Staff Form */}
    <div className="mb-5 position-relative p-3 rounded-3 bg-gray-50 shadow-sm" ref={inputRef}>
      <label className="form-label fw-semibold text-gray-700">👤 Staff Name</label>
      <input
        type="text"
        className="form-control mb-2 rounded-3 shadow-sm"
        placeholder="Enter staff name"
        value={newStaffName}
        onChange={(e) => {
          setNewStaffName(e.target.value);
          setShowSuggestions(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
      />

      {/* Suggestions */}
      {showSuggestions && newStaffName && (
        <ul className="list-group position-absolute w-100 shadow border rounded-3" style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}>
          {filteredSuggestions.map((st, idx) => {
            const lowerName = st.name.toLowerCase();
            const lowerInput = newStaffName.toLowerCase();
            const startIndex = lowerName.indexOf(lowerInput);
            let before = "", match = st.name, after = "";
            if (startIndex >= 0) {
              before = st.name.slice(0, startIndex);
              match = st.name.slice(startIndex, startIndex + newStaffName.length);
              after = st.name.slice(startIndex + newStaffName.length);
            }
            return (
              <li
                key={idx}
                className={`list-group-item list-group-item-action ${idx === highlightIndex ? "active" : ""}`}
                onClick={() => {
                  setNewStaffName(st.name);
                  setShowSuggestions(false);
                  setHighlightIndex(-1);
                }}
                style={{ cursor: "pointer" }}
              >
                {before}<strong className="text-primary">{match}</strong>{after}
                <span className="text-muted"> – {st.role}</span>
              </li>
            );
          })}
        </ul>
      )}

      <label className="form-label fw-semibold text-gray-700 mt-3">🛠 Role</label>
       <CreatableSelect
  isClearable
  options={roleOptions}
  value={newStaffRole}
  onChange={(selected) => setNewStaffRole(selected)}
  placeholder="Select or create role..."
  className="basic-select"
  classNamePrefix="select"
  menuPortalTarget={document.body}   // ✅ forces menu to render at root
  styles={{
    menuPortal: base => ({ ...base, zIndex: 9999 })  // ✅ ensures it stays above table
  }}
/>

      <button
        className="btn btn-success mt-3 shadow-sm"
        disabled={!newStaffName || !newStaffRole || staffList.some(st => st.name.toLowerCase() === newStaffName.toLowerCase())}
        onClick={() => {
          if (!newStaffName || !newStaffRole) {
            Swal.fire("⚠️ Please enter name and role");
            return;
          }
          const exists = staffList.some(s => s.name.toLowerCase() === newStaffName.toLowerCase());
          if (exists) {
            Swal.fire("⚠️ Already Exists", "This staff member is already in the list.", "warning");
            return;
          }
          axios.post("https://ricgop.online/backend/staff/addStaff.php", {
            name: newStaffName,
            role: newStaffRole.value,
          })
          .then(() => {
            Swal.fire("✅ Added!", "Staff saved successfully", "success");
            setStaffList((prev) => [...prev, { name: newStaffName, role: newStaffRole.value }]);
            setStaff((prev) => prev.includes(newStaffName) ? prev : [...prev, newStaffName]);
            setNewStaffName(""); setNewStaffRole(null);
          })
          .catch(() => {
            Swal.fire("❌ Error", "Failed to add staff", "error");
          });
        }}
      >
        ➕ Add Staff
      </button>
    </div>


{/* Roster Table */}
{/* Roster Table */}
{staff.length > 0 && (
  <div className="roster-wrapper mb-4 overflow-x-auto">
    <table className="roster-table min-w-full border-collapse">
      <thead className="sticky top-0 bg-white z-10">
        <tr>
          <th className="px-2 py-2 text-left border">Staff</th>

          {/* added days */}
         {Array.from({ length: numDays }, (_, i) => {
  const date = new Date(month);
  const [monthName, year] = month.split(" ");
  const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
  const currentDate = new Date(year, monthIndex, i + 1);

  const dayNamesEn = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const dayNamesUr = ["اتوار","پیر","منگل","بدھ","جمعرات","جمعہ","ہفتہ"];
  const dayName = isUrdu ? dayNamesUr[currentDate.getDay()] : dayNamesEn[currentDate.getDay()];

  return (
    <th key={i} className="day-pill px-2 py-2 text-center border min-w-[60px]">
      <span>{i + 1}</span>
      <div className="text-xs text-gray-600">{dayName}</div>
    </th>
  );
})}
        </tr>
      </thead>
      <tbody>
        {staff.map((person, index) => (
          <tr key={index} className="odd:bg-gray-50">
            <td className="staff-cell px-2 py-2 border whitespace-nowrap">
              {person}
            </td>
            {Array.from({ length: numDays }, (_, i) => {
              const dayValue = days[person]?.[i + 1] || "";
              return (
                <td
                  key={i}
                  className="px-1 py-4 text-center align-middle border min-w-[60px]"
                  style={{
                    backgroundColor: holidayMap[month]?.[i + 1]
                      ? "#fff8e1"
                      : "inherit",
                  }}
                >
           <Select
  value={
    dayValue
      ? {
          value: dayValue,
          label: isUrdu && dutyMapUrdu[dayValue] ? dutyMapUrdu[dayValue] : dayValue,
        }
      : null
  }
  onChange={(selected) =>
    updateDay(person, i + 1, selected ? selected.value : "")
  }
  options={statusOptions.map((opt) => ({
    value: opt,
    label: isUrdu && dutyMapUrdu[opt] ? dutyMapUrdu[opt] : opt,
  }))}
  className="status-dropdown"
  classNamePrefix="select"
  menuPortalTarget={document.body}
  components={{
    ClearIndicator: () => null,
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
  }}
  styles={{
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      minWidth: "160px",
      direction: isUrdu ? "rtl" : "ltr", // ✅ RTL for Urdu
      textAlign: isUrdu ? "right" : "left",
    }),
    control: (base) => ({
      ...base,
      minHeight: "30px",
      height: "30px",
      fontSize: "12px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 4px",
      display: "block",
      alignItems: "center",
      justifyContent: "center",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "12px",
      fontWeight: "500",
    }),
    option: (base) => ({
      ...base,
      fontSize: "13px",
      padding: "6px 10px",
      textAlign: isUrdu ? "right" : "left",
    }),
  }}
/>
                  {holidayMap[month]?.[i + 1] && (
                    <div className="holiday-note text-xs text-gray-500 mt-1">
                      {holidayMap[month][i + 1]}
                    </div>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}



   <style>{`

/* --- Modern Roster Table --- */
@media (min-width: 1400px) {
    .container, .container-lg, .container-md, .container-sm, .container-xl, .container-xxl {
        max-width: 1750px;
    }
}


.roster-wrapper {
  background: #ffffff;
  border-radius: 16px;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  width: 100%;
}

/* Table Base */
.roster-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;   /* keeps all columns equal */
  font-size: 11px;
}

/* Header */
.roster-table thead {
  background: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.roster-table th {
  padding: 6px 2px;
  font-weight: 600;
  font-size: 11px;
  text-align: center;
  border-bottom: 2px solid #e5e7eb;
}

/* --- Calendar Pill for Days --- */
.roster-table th.day-pill {
  background: transparent;
  border: none;
}

.roster-table th.day-pill span {
  display: block;
  padding: 4px 6px;
  min-width: 22px;
  border-radius: 8px;
  background: #e0e7ff;
  color: #1e3a8a;
  font-size: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
}

.roster-table th.day-pill span:hover {
  background: #2563eb;
  color: #fff;
}

/* Rows */
.roster-table td {
  border: 1px solid #e5e7eb;
  padding: 2px;
  text-align: center;
  vertical-align: middle;
  height: 24px;
}

/* Zebra & hover */
.roster-table tbody tr:nth-child(even) {
  background: #f9fafb;
}
.roster-table tbody tr:hover {
  background: #f3f4f6;
}

/* Sticky staff column */
.roster-table .staff-cell {
  font-weight: 600;
  color: #1d4ed8;
  text-align: left;
  padding-left: 6px;
  background: #f8fafc;
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 120px;
  border-right: 1px solid #e5e7eb;
}

/* Compact Dropdown */
.roster-table .status-dropdown .select__control {
  min-height: 18px !important;
  height: 18px !important;
  font-size: 9px !important;
  border-radius: 4px !important;
  border: 1px solid #d1d5db !important;
}

.roster-table .status-dropdown .select__dropdown-indicator,
.roster-table .status-dropdown .select__clear-indicator {
  padding: 0 2px !important;
}

.py-4{
padding-top: 1.5rem !important;
padding-bottom: 1.5rem !important;
}

/* Holiday label */
.roster-table .holiday-note {
  font-size: 7px;
  color: #dc2626;
  line-height: 1;
  margin-top: 1px;
}

/* --- Colored Badges --- */
.status-badge {
  display: inline-block;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  color: #fff;
}
.status-E { background-color: #22c55e; }
.status-M { background-color: #3b82f6; }
.status-N { background-color: #a855f7; }
.status-O { background-color: #9ca3af; }


      `}</style>

    {/* Action Buttons */}
    <div className="d-flex flex-wrap gap-3 mb-5">
      <button className="btn btn-primary shadow-sm" onClick={downloadPDFs}>📥 Download PDFs</button>
      <button className="btn btn-success shadow-sm" onClick={saveRosterToDB}>💾 Save Roster to DB</button>
      <button className="btn btn-secondary shadow-sm" onClick={() => fetchRoster(null, month)}>📥 Load Saved Roster for {month}</button>
    </div>

    {/* Ward Boy Summary */}
    <div className="mb-3">
      <label className="form-label fw-semibold text-gray-700">🔍 Select Ward Boy</label>
      <Select
        options={staff.map(name => ({ value: name, label: name }))}
        value={selectedStaff}
        onChange={handleStaffSelect}
        placeholder="Select ward boy..."
        isClearable
      />
      {selectedStaff && (
        <p className="fw-semibold mt-2">
          {selectedStaff.label} attended <span className="text-success">{eCount}</span> "E" days in {month}.
        </p>
      )}
    </div>
  </div>
);


};

export default BiomedicalDepartment;
